import React from 'react';
import PropTypes from 'prop-types';

import tabDetails from './Navigation/tabDetails';
import reportDetails from './Navigation/reportDetails';
import { levelFilters, sortingMethods, whatImDoingFilters } from './GrowthReports/reportDetails';
import accordionUtils from './Accordion/utils';
import reportAccessUtils from '../utils/reportAccess';
import { roleLevels } from '../resources/roles';

import ErrorPage from './ErrorPage';
import RLPError from './ErrorPage/RLPError';

import Utils from './utils';

import Routes from './Routes';
import { BrowserRouter, Route } from 'react-router-dom';

const appDataMenuShape = PropTypes.shape({
  secondaryMenuItems: PropTypes.arrayOf(
    PropTypes.shape({
      displayText: PropTypes.string.isRequired,
      external: PropTypes.bool.isRequired,
      link: PropTypes.string.isRequired,
    })
  ).isRequired,
});

const appDataShape = PropTypes.shape({
  dataExportHistory: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  defaultSelectedTabEndpoint: PropTypes.string.isRequired,
  menus: appDataMenuShape.isRequired,
  userAvailableTabs: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  userGrowthReportDetails: PropTypes.arrayOf(PropTypes.shape()),
  userName: PropTypes.string.isRequired,
  userOperationalReportDetails: PropTypes.arrayOf(PropTypes.shape()),
  partnerBid: PropTypes.string.isRequired,
});

const propTypes = {
  educatorBid: PropTypes.string.isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  appData: appDataShape.isRequired,
};

class MainContainer extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      accordions: this.getAccordionsState(),
      bannerShown: true,
      hasError: false,
      growthSorting: {
        isExpanded: false,
        value: sortingMethods.recommended,
        previousValue: sortingMethods.recommended,
      },
    };
  }

  componentDidCatch(err) {
    this.setState({ hasError: true, errorObject: new RLPError({ payload: this.state }, err.message) });
  }

  /**
   * This function defines the state for accordion controls within the side bars for the various tabs.
   * A Map for each tab holding the sidebar accordion items is stored in state
   *  - key: The accordions unique identifier (All available identifiers are defined within the Accordions/utils file)
   *  - value: The accordions state
   *
   *  i.e.
   * {
   *  map: Map(
   *          [key: dataExportScheduler,
   *           value: {
   *                      displayText: 'Data Export Scheduler',
   *                      itemState: {
   *                          isExpanded: true,
   *                          exportHistory: [...]
   *                          }
   *                   }
   *           ],
   *          [key: reportsQueue,
   *           value {
   *                      displayText: 'Reports Queue',
   *                      itemState: {
   *                          isExpanded: false,
   *                          queueDetails:{...}
   *                          }
   *                  }
   *           ])
   *  operational: Map(
   *          [key: reportsQueue,
   *           value {
   *                      displayText: 'Reports Queue',
   *                      itemState: {
   *                          isExpanded: false,
   *                          queueDetails:{...}
   *                          }
   *                  }
   *           ])
   * }
   *
   * The values held within this map are rendered into components via the Accordions component
   * within the tab in question.
   */
  getAccordionsState = () => {
    const allAccordions = {};

    const growthAccordions = new Map();
    const operationalAccordions = new Map();

    if (this.props.appData.userGrowthReportDetails && this.props.appData.userGrowthReportDetails.length > 0) {
      const userFilterRoleLevel = reportAccessUtils.getUserRoleLevel(this.props.userRoles);

      if (userFilterRoleLevel !== roleLevels.none) {
        accordionUtils.addAccordionState(accordionUtils.REPORT_LEVEL_FILTER, growthAccordions, {
          value: levelFilters.all,
          defaultValue: levelFilters.all,
          roleLevel: userFilterRoleLevel,
          mutuallyExclusiveFilter: {
            id: accordionUtils.WHAT_IM_DOING_FILTER,
            mutuallyExclusiveFilterValues: whatImDoingFilters,
            mutuallyExclusiveFilterDisabledMap: new Map(),
          },
        });
        accordionUtils.addAccordionState(accordionUtils.WHAT_IM_DOING_FILTER, growthAccordions, {
          value: whatImDoingFilters.all,
          defaultValue: whatImDoingFilters.all,
          mutuallyExclusiveFilter: {
            id: accordionUtils.REPORT_LEVEL_FILTER,
            mutuallyExclusiveFilterValues: levelFilters,
            mutuallyExclusiveFilterDisabledMap: new Map(),
          },
        });
      }

      if (
        this.props.userRoles.some((role) => ['ASSESSMENT_COORDINATOR', 'SCHOOL_ASSESSMENT_COORDINATOR'].includes(role))
      ) {
        accordionUtils.addAccordionState(accordionUtils.DATA_EXPORT_SCHEDULER, growthAccordions, {
          exportHistory: this.props.appData.dataExportHistory,
        });
      }
    }

    allAccordions[tabDetails.growthReports.urlEndPoint] = growthAccordions;
    allAccordions[tabDetails.operationalReports.urlEndPoint] = operationalAccordions;

    return allAccordions;
  };

  handleSortSelection = (isExpanded, value) => {
    this.setState({
      growthSorting: { isExpanded: isExpanded, value: value, previousValue: this.state.growthSorting.value },
    });
  };

  handleResetFilters = (containingTab) => {
    const accordionsState = { ...this.state.accordions };
    if (accordionsState[containingTab]) {
      accordionUtils.filterIdentifiers.forEach((accordionIdentifier) => {
        const filterAccordion = accordionsState[containingTab].get(accordionIdentifier);
        if (filterAccordion) {
          filterAccordion.itemState.value = filterAccordion.itemState.defaultValue;
          accordionsState[containingTab].set(accordionIdentifier, filterAccordion);
        }
      });

      this.setState({ accordions: accordionsState });
    }
  };

  /**
   * This function uses the supplied updateFn to change the itemState of the accordion item found
   * by looking for the specified accordionIdentifier within the Map for the supplied containingTab.
   */
  handleAccordionStateChange = (containingTab, accordionIdentifier, updateFn = (itemState) => {}) => {
    const accordionsState = { ...this.state.accordions };
    if (accordionsState[containingTab]) {
      const accordion = accordionsState[containingTab].get(accordionIdentifier);
      if (accordion) {
        updateFn(accordion.itemState);
        accordionsState[containingTab].set(accordionIdentifier, accordion);

        this.setState({ accordions: accordionsState });
      }
    }
  };

  getFilteredGrowthReports = () => {
    const reportLevelFilterState = this.state.accordions[tabDetails.growthReports.urlEndPoint].get(
      accordionUtils.REPORT_LEVEL_FILTER
    );
    const whatImDoingFilterState = this.state.accordions[tabDetails.growthReports.urlEndPoint].get(
      accordionUtils.WHAT_IM_DOING_FILTER
    );

    return Utils.filterGrowthReports(
      this.props.appData.userGrowthReportDetails,
      reportLevelFilterState ? reportLevelFilterState.itemState.value : levelFilters.all,
      whatImDoingFilterState ? whatImDoingFilterState.itemState.value : levelFilters.all,
      this.state.growthSorting.value
    );
  };

  setSortExpandState = (isExpanded) => {
    this.setState({
      growthSorting: {
        isExpanded: isExpanded,
        value: this.state.growthSorting.value,
        previousValue: this.state.growthSorting.previousValue,
      },
    });
  };

  setBannerClose = () => {
    this.setState({
      bannerShown: false,
    });
  };

  render() {
    const filteredGrowthReports = this.getFilteredGrowthReports();
    if (this.state.hasError) {
      // In case of a breaking error we'd want to
      // return other rendering options.
      return <ErrorPage thrownError={this.state.errorObject} />;
    }

    return (
      <BrowserRouter>
        <Route
          path={'/'}
          render={({ location }) => (
            <Routes
              bannerClose={this.setBannerClose}
              bannerShown={this.state.bannerShown}
              menus={this.props.appData.menus}
              userName={this.props.appData.userName}
              partnerBid={this.props.appData.partnerBid}
              location={location}
              availableTabs={this.props.appData.userAvailableTabs}
              reportDetails={reportDetails}
              allGrowthReportDetailsForUser={this.props.appData.userGrowthReportDetails}
              filteredGrowthReportDetails={filteredGrowthReports}
              growthReportAccordionDetails={this.state.accordions[tabDetails.growthReports.urlEndPoint]}
              operationalReportAccordionDetails={this.state.accordions[tabDetails.operationalReports.urlEndPoint]}
              operationalReportDetails={this.props.appData.userOperationalReportDetails}
              defaultSelectedTabEndpoint={this.props.appData.defaultSelectedTabEndpoint}
              onAccordionStateChange={this.handleAccordionStateChange}
              onResetFilters={this.handleResetFilters}
              onSortSelection={this.handleSortSelection}
              sortSelection={this.state.growthSorting}
              sortExpandStateFn={this.setSortExpandState}
              educatorBid={this.props.educatorBid}
              sessionToken={this.props.appData.sessionToken}
            />
          )}
        />
      </BrowserRouter>
    );
  }
}

MainContainer.propTypes = propTypes;

export default MainContainer;
